import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form990ShcM = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 990 Schedule M: Guide for Noncash Contributions"
          description="Understand the requirements for reporting noncash contributions on Form 990 Schedule M. This guide explains everything you need to know."
          keywords=""
          canonical="resources/tax-exempt/form-990-schedule-m/"
          ogType="article"
          jsonLdPricing="179.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form 990{' '}
          <span className="highlight-container">
            <span className="highlight">Schedule M</span>
          </span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Report Noncash Contributions with TaxZerone!
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-990-schedule-m/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 990 Schedule M at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Purpose: Form 990 Schedule M
                        </strong>{' '}
                        is used to report{' '}
                        <strong style={{ fontWeight: 600 }}>
                          noncash contributions
                        </strong>{' '}
                        received by a tax-exempt organization during the tax
                        year, including the types of donated property and
                        related contribution information.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Must File?
                        </strong>{' '}
                        Organizations that answer{' '}
                        <strong style={{ fontWeight: 600 }}>"Yes"</strong> to{' '}
                        <Link
                          href="https://testweb.variniral.com/resources/tax-exempt/form-990-instructions/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 990
                        </Link>
                        <strong style={{ fontWeight: 600 }}>
                          , Part IV, line 29
                        </strong>{' '}
                        generally must complete and attach{' '}
                        <strong style={{ fontWeight: 600 }}>Schedule M</strong>.
                        This typically applies if the organization receives{' '}
                        <strong style={{ fontWeight: 600 }}>
                          more than $25,000
                        </strong>{' '}
                        in noncash contributions during the tax year.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Reported?
                        </strong>{' '}
                        Schedule M reports{' '}
                        <strong style={{ fontWeight: 600 }}>
                          noncash donations
                        </strong>{' '}
                        such as{' '}
                        <strong style={{ fontWeight: 600 }}>
                          artwork, vehicles, securities, real estate, inventory,
                          intellectual property, conservation easements
                        </strong>
                        , and other donated property, along with the applicable
                        reporting details.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Reporting Threshold:
                        </strong>{' '}
                        Organizations generally file{' '}
                        <strong style={{ fontWeight: 600 }}>Schedule M</strong>{' '}
                        when{' '}
                        <strong style={{ fontWeight: 600 }}>
                          total noncash contributions exceed $25,000
                        </strong>{' '}
                        for the tax year, subject to the IRS reporting
                        requirements.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>

                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Schedule M?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Schedule M?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#scha" sx={{ color: '#0202ff' }}>
                          Schedule M Filing Requirements
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#choose" sx={{ color: '#0202ff' }}>
                          Choose TaxZerone to complete your Schedule M filing
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Commonly Asked Questions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="what"
                >
                  What is Schedule M?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong style={{ fontWeight: 600 }}>Schedule M </strong>(Form
                  990) is used by an organization that files{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990
                  </Link>{' '}
                  to report the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    types of noncash contributions
                  </strong>
                  received during the year by the organization and certain
                  information regarding such contributions.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  It also requires reporting of the quantity and the reported
                  financial statement amount of noncash contributions received
                  by type of property.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="who"
                >
                  Who must file Schedule M?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  An organization that answered “Yes” to{' '}
                  <strong style={{ fontWeight: 600 }}>Form 990, Part IV</strong>
                  ,<i>lines 29 or 30</i>, must complete Schedule M (Form 990)
                  and attach it to Form 990.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Two main scenarios trigger the requirement to file Schedule M:
                </Typography>
                <Box component="ol" sx={{ mb: 3 }}>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Aggregate Non-Cash Contributions:
                    </strong>{' '}
                    If the total value of non-cash contributions received during
                    the tax year exceeds $25,000 on Form 990, Part VIII, line
                    1g, you must file Schedule M.
                  </li>
                  <li>
                    <strong style={{ fontWeight: 600 }}>
                      Specific Assets:
                    </strong>{' '}
                    Even with non-cash contributions below the $25,000
                    threshold, you still need to file Schedule M if your
                    organization receives any of the following contributions in
                    Form 990 Part VIII:
                    <Box component="ol" sx={{ mt: 2, mb: 3 }} type="a">
                      <li style={{ marginBottom: 16 }}>
                        Artwork, historical treasures, or similar valuable
                        assets.
                      </li>
                      <li>
                        Qualified conservation contributions (land or easements
                        for conservation purposes).
                      </li>
                    </Box>
                  </li>
                </Box>

                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    mb: 5,
                    border: '1px solid #ccc',
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Streamline Your 990 Filing with TaxZerone!
                    </strong>
                    <br />
                    Make your e-filing process simple by clicking the button
                    below.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      textTransform: 'none',
                      boxShadow: 'none',
                      borderRadius: 2,
                    }}
                    size="large"
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990schm`}
                  >
                    E-File Your Form 990
                  </Button>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="scha"
                >
                  Schedule M Filing Requirements
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schm-details-img.webp" />
                  <source srcSet="/assets/images/f990-schm-details-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schm-details-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule M showing organization details and basic information"
                    width={760}
                    height={124}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Below, we have provided Schedule M filing requirements for
                  each part.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part I - Types of Property
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schm-part1-img.webp" />
                  <source srcSet="/assets/images/f990-schm-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schm-part1-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule M, Part I, listing and describing the types of property donated to the organization."
                    width={760}
                    height={66}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Provide information on the non-cash contributions you received
                  and complete the information asked in the respective columns
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (a)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Check if applicable
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (b)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Number of contributions or items contributed
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (c)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Noncash contribution amounts reported on Form 990, Part VIII,
                  line 1g{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (d)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Method of determining noncash contribution amounts
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schm-part1-line1-img.webp" />
                  <source srcSet="/assets/images/f990-schm-part1-line1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schm-part1-line1-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule M, Part I, listing and describing the types of property donated to the organization. Line1 - Line14"
                    width={760}
                    height={281}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report donated art items such as paintings, sculptures,
                  photos, antiques, textiles, rare books, and similar objects.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report donated buildings or properties that have recognized
                  historical, cultural, or architectural importance.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report when a donor gives partial ownership of a work of art
                  instead of the full piece. List the percentage of ownership
                  received each year.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report donated books and publications. Don’t include rare
                  books already reported as art or archival records reported
                  elsewhere.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report donated clothing or household items only if they are in
                  good used condition or better. Items not in good condition
                  must be reported separately under “Other.”
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report donated cars or other vehicles mainly used on public
                  roads. The organization must issue and file{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1098-c/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 1098-C
                  </Link>{' '}
                  for these donations.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report donated boats and airplanes (also requires{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1098-c/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 1098-C
                  </Link>
                  ).
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 8
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report donated patents, copyrights, trademarks, trade secrets,
                  or similar rights. The organization may need to file Form
                  8899.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 9
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report donations of stocks or investment interests that are
                  traded on an established public market.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 10
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report donated shares of a company that is not publicly
                  traded.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 11
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report donations of ownership interests in private
                  partnerships, LLCs, or trusts that are not publicly
                  traded.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 12
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report donated securities not already listed in Lines 9–11.
                  Each separate gift is reported individually.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 13
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report donations where a donor gives a permanent restriction
                  (easement) on the exterior of a certified historic building or
                  structure to help preserve its historical appearance or
                  value.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 14
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Report other qualified conservation donations, such as land or
                  easements given permanently to protect nature or public
                  benefit purposes{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schm-part1-line15-img.webp" />
                  <source srcSet="/assets/images/f990-schm-part1-line15-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schm-part1-line15-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule M, Part I, listing and describing the types of property donated to the organization. Line15 - Line28"
                    width={760}
                    height={229}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 15
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report donations of homes or personal residences, including
                  partial interests (like remainder interests). This includes
                  vacation homes or cooperative housing units used as a
                  residence.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 16
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report donations of business properties, such as office
                  buildings, or farmland used for crops or livestock, including
                  buildings or improvements on the farm.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 17
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report real estate donations not covered in Lines 15 or 16,
                  such as land or other property interests.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 18
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report donated collectibles like coins, stamps, sports
                  memorabilia, autographs, gems, jewelry, or similar items{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 19
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the value of donated food or food inventory, including
                  food contributions received from businesses or companies.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 20
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report donated medicines, medical supplies, or similar items
                  given by companies that manufacture or distribute them.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 21
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This is for any donated mounts or displays that use real
                  animal part{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 22
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {' '}
                  Use this for antique household goods or decor, like vintage
                  furniture, textiles, or old home fixtures. Just remember to
                  keep artwork or ancient archaeological finds out of this
                  specific box
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 23
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This covers items used for learning or research. It includes
                  things like plant and animal samples, or geological finds like
                  rocks and minerals
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 24
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This is the spot for ancient objects recovered through
                  excavations, as well as cultural pieces from traditional or
                  tribal communities that carry significant historical weight
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 25-28
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Use these lines for donated items not listed earlier, such as
                  clothing or household goods not in good condition,
                  nonqualified conservation easements, self-created materials or
                  items used at charity auctions. Donated services or use of
                  equipment or space shouldn’t be included here.{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schm-part1-line29-img.webp" />
                  <source srcSet="/assets/images/f990-schm-part1-line29-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schm-part1-line29-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule M, Part I, listing and describing the types of property donated to the organization. Line29 - Line33"
                    width={760}
                    height={229}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 29
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter how many Form 8283s your organization received during
                  the year for noncash donations that you reviewed and signed,
                  as required by the Internal Revenue Service (IRS).
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 30a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select “Yes” if a donated noncash item must be kept for at
                  least three years because of donor conditions or another
                  agreement.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 30b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you answered “Yes” on Line 30a, briefly explain in Part II
                  why the property must be held and any rules attached to the
                  donation.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 31
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer “Yes” if the organization reviews unusual or difficult
                  donations, such as items that don’t support its mission, are
                  hard to sell, or have an uncertain value.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 32a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer “Yes” if your organization works with outside groups or
                  related organizations to collect, process, or sell donated
                  items. Answer “No” if the only outside help is a broker
                  selling donated publicly traded securities. If “Yes,” briefly
                  explain the arrangement in Part II.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 32b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {' '}
                  If you answered “Yes” on 32a, use Part II to describe how
                  these third parties handle the donations.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 33
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If any donation type didn’t show revenue in column (c),
                  explain in Part II why it wasn’t reported.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part II - Supplemental Information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schm-part2-img.webp" />
                  <source srcSet="/assets/images/f990-schm-part2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schm-part2-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule M- Part II Details of Supplemental Information"
                    width={760}
                    height={407}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use this part to provide narrative information required in
                  Part I, column (b), and Part I, lines 30b, 32b, and 33, and
                  whether the organization is reporting in Part I, column (b),
                  the number of contributions, the number of items received, or
                  a combination of both.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Also use Part II to provide other narrative explanations and
                  descriptions, as needed. Identify the specific line number
                  that the response supports. This part can be duplicated in
                  case of space requirements.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="choose"
                >
                  Choose TaxZerone to complete your Schedule M filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  We at <strong style={{ fontWeight: 600 }}>TaxZerone</strong>{' '}
                  help to revolutionize your tax filing experience, making it as
                  simple and stress-free as possible.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Here’s how your Form 990 return with Schedule M attachment is
                  transmitted to the IRS in 3 simple steps!
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Provide Organization Details -
                    </strong>{' '}
                    Choose the tax year for which you want to file a return, and
                    provide your organization’s details.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Preview the Return -
                    </strong>{' '}
                    Complete Schedule M and preview the information provided in
                    the return for accuracy before transmitting.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Transmit to the IRS -
                    </strong>{' '}
                    Transmit Schedule M and your 990 return to the IRS and get
                    the acceptance in just a few hours.
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In the unlikely event of an IRS rejection, we&apos;ll help you
                  identify and correct any errors in your Form 990 information
                  return, ensuring a swift and cost-free resubmission.
                </Typography>

                <Box
                  component="div"
                  sx={{ mb: 2, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0 }}
                      >
                        Embark on a Smooth Schedule M Filing Journey with
                        TaxZerone
                      </Typography>
                      <Typography variant="body2">
                        Complete your Schedule M filing requirements with ease!
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 120, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990schm`}
                      >
                        E-file Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided on this page is based on
                        guidance from the following IRS resources:
                      </Typography>
                      <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/f990sm.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Schedule M (Form 990) – Noncash Contributions
                        </li>{' '}
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/i990.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Form 990 – Return of Organization Exempt From
                          Income Tax
                        </li>{' '}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
                scrollMarginTop: 83,
              }}
              id="common"
            >
              Commonly Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What types of non-cash contributions are reported on
                  Schedule M?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Schedule M provides a comprehensive list of reportable
                    non-cash contributions. It includes items like:
                    <ul>
                      <li>Inventory</li>
                      <li>Securities (stocks, bonds)</li>
                      <li>Real estate</li>
                      <li>Equipment</li>
                      <li>Vehicles</li>
                      <li>
                        Intangible assets (intellectual property, patents)
                      </li>
                    </ul>
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. Do I need to report donated services on Schedule M?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    No. Schedule M focuses solely on donated items or assets.
                    Don’t report donations of services or the donated use of
                    facilities, equipment, or materials donated in Schedule M.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. What qualifies as a noncash contribution for Schedule M?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Noncash contributions include donations of property, goods,
                    or services, such as equipment, supplies, real estate, or
                    professional services, that are received by the organization
                    without the transfer of cash.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form990ShcM;
